import { useCallback, useState } from 'react';
import { searchUsers, sendFriendRequest } from '@domain/contacts/api';
import type { UserSearchResult } from '@domain/contacts/types';
import { searchGroups } from '@domain/group/api';
import type { GroupSearchResult } from '@domain/group/types';
import type { LogEntry } from '@shared/config/runtime';
import type { QueryState } from '@shared/types/async';
import { getErrorMessage } from '@shared/utils/ui';

type UseWorkspaceDirectoryOptions = {
  apiBase: string;
  pushLog: (msg: string, kind?: LogEntry['kind']) => void;
  refreshPendingCount: () => Promise<void>;
};

export function useWorkspaceDirectory({
  apiBase,
  pushLog,
  refreshPendingCount,
}: UseWorkspaceDirectoryOptions) {
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searchResults, setSearchResults] = useState<UserSearchResult[]>([]);
  const [searchState, setSearchState] = useState<QueryState>('idle');
  const [searchError, setSearchError] = useState<string | null>(null);

  const [groupSearchKeyword, setGroupSearchKeyword] = useState('');
  const [groupSearchResults, setGroupSearchResults] = useState<GroupSearchResult[]>([]);
  const [groupSearchState, setGroupSearchState] = useState<QueryState>('idle');
  const [groupSearchError, setGroupSearchError] = useState<string | null>(null);

  const [sendingRequestByUserId, setSendingRequestByUserId] = useState<Record<number, boolean>>({});

  const resetDirectoryState = useCallback(() => {
    setSearchKeyword('');
    setSearchResults([]);
    setSearchState('idle');
    setSearchError(null);
    setGroupSearchKeyword('');
    setGroupSearchResults([]);
    setGroupSearchState('idle');
    setGroupSearchError(null);
    setSendingRequestByUserId({});
  }, []);

  const handleSearchUsers = useCallback(async () => {
    const keyword = searchKeyword.trim();
    if (!keyword) {
      setSearchState('idle');
      setSearchResults([]);
      setSearchError(null);
      return;
    }

    setSearchState('loading');
    setSearchError(null);
    try {
      const results = await searchUsers(keyword, apiBase);
      setSearchResults(results);
      setSearchState('done');
    } catch (error) {
      setSearchState('error');
      setSearchError(getErrorMessage(error));
    }
  }, [apiBase, searchKeyword]);

  const handleSearchGroups = useCallback(async () => {
    const keyword = groupSearchKeyword.trim();
    if (!keyword) {
      setGroupSearchState('idle');
      setGroupSearchResults([]);
      setGroupSearchError(null);
      return;
    }

    setGroupSearchState('loading');
    setGroupSearchError(null);
    try {
      const results = await searchGroups(keyword, apiBase);
      setGroupSearchResults(results);
      setGroupSearchState('done');
    } catch (error) {
      setGroupSearchState('error');
      setGroupSearchError(getErrorMessage(error));
    }
  }, [apiBase, groupSearchKeyword]);

  const handleSendFriendRequest = useCallback(async (targetUserId: number, note: string) => {
    setSendingRequestByUserId((prev) => ({ ...prev, [targetUserId]: true }));
    try {
      await sendFriendRequest(targetUserId, note, apiBase);
      pushLog(`关系申请已发送给用户 #${targetUserId}`, 'success');
      setSearchResults((prev) => prev.filter((entry) => entry.id !== targetUserId));
      await refreshPendingCount();
    } catch (error) {
      pushLog(`发送关系申请失败: ${getErrorMessage(error)}`, 'error');
    } finally {
      setSendingRequestByUserId((prev) => {
        const next = { ...prev };
        delete next[targetUserId];
        return next;
      });
    }
  }, [apiBase, pushLog, refreshPendingCount]);

  return {
    searchKeyword,
    setSearchKeyword,
    searchResults,
    setSearchResults,
    searchState,
    setSearchState,
    searchError,
    setSearchError,
    groupSearchKeyword,
    setGroupSearchKeyword,
    groupSearchResults,
    setGroupSearchResults,
    groupSearchState,
    setGroupSearchState,
    groupSearchError,
    setGroupSearchError,
    sendingRequestByUserId,
    resetDirectoryState,
    handleSearchUsers,
    handleSearchGroups,
    handleSendFriendRequest,
  };
}
